import * as React from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { CircleAlert } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";

import { z } from "zod";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase/supabaseClient";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import LoadingSpinner from "@/components/LoadingSpinner";

export const Route = createFileRoute("/(unauth)/forgot")({
  component: ForgotPage,
  beforeLoad({ context }) {
    if (context.session) {
      throw redirect({ to: "/clients", replace: true });
    }
  },
});

function ForgotPage() {
  const formSchema = z.object({
    email: z.string().email(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const [complete, setComplete] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const redirectUrl = window.location.origin + "/reset-password";

    console.log(redirectUrl);
    setLoading(true);

    const { data, error } = await supabase.auth.resetPasswordForEmail(
      values.email,
      {
        redirectTo: redirectUrl,
      }
    );
    setLoading(false);

    if (error) {
      console.error(error);
      toast.error(error.message);
      form.setError("email", { message: error.message });
      return;
    }

    setComplete(true);
  }

  return (
    <>
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <a
            href="#"
            className="flex items-center gap-2 self-center font-medium"
          >
            <img src="/lockupBlack.svg" alt="Trainrr" className="h-6" />
          </a>

          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Forgot your password</CardTitle>
              <CardDescription>
                Enter your email to reset your password
              </CardDescription>
            </CardHeader>
            <CardContent>
              {complete && (
                <Alert variant="info" className="mb-6">
                  <CircleAlert className="h-4 w-4" />
                  <AlertTitle>Email Sent</AlertTitle>
                  <AlertDescription>
                    An email was sent to {form.getValues("email")}. Please check
                    your inbox to reset your password.
                  </AlertDescription>
                </Alert>
              )}

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>

                          <FormControl>
                            <Input
                              placeholder="name@example.com"
                              type="email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button>
                      {loading && <LoadingSpinner />}
                      {!loading && "Reset Password"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
